import { normalizeDataType } from "./expressionEditorUtils";
import { useCallback, useMemo, useState } from "react";

const defaultMatchesText = (col, lower) =>
  (col.displayName ?? "").toLowerCase().includes(lower) ||
  (col.internalName ?? "").toLowerCase().includes(lower);

export function countColumnsByType(
  columns,
  getDataType = (col) => col.dataType
) {
  const counts = {};
  columns.forEach((col) => {
    const type = normalizeDataType(getDataType(col));
    if (type) counts[type] = (counts[type] ?? 0) + 1;
  });
  return counts;
}

export function useColumnFilter(
  columns,
  { getDataType = (col) => col.dataType, matchesText = defaultMatchesText } = {}
) {
  const [filterText, setFilterText] = useState("");
  const [selectedDisplayTypes, setSelectedDisplayTypes] = useState(new Set());

  const typeCounts = useMemo(() => countColumnsByType(columns, getDataType), [
    columns,
    getDataType,
  ]);

  const filteredColumns = useMemo(() => {
    let cols = columns;
    if (filterText.trim()) {
      const lower = filterText.trim().toLowerCase();
      cols = cols.filter((col) => matchesText(col, lower));
    }
    if (selectedDisplayTypes.size > 0) {
      cols = cols.filter((col) =>
        selectedDisplayTypes.has(normalizeDataType(getDataType(col)))
      );
    }
    return cols;
  }, [columns, filterText, selectedDisplayTypes, getDataType, matchesText]);

  const handleToggleType = useCallback((type) => {
    setSelectedDisplayTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const handleSelectAnyMode = useCallback(
    () => setSelectedDisplayTypes(new Set()),
    []
  );

  return {
    filterText,
    setFilterText,
    selectedDisplayTypes,
    typeCounts,
    filteredColumns,
    handleToggleType,
    handleSelectAnyMode,
    isAnyTypeMode: selectedDisplayTypes.size === 0,
  };
}
